import type { z } from "zod";

type Issue = z.core.$ZodIssue;

/**
 * Nazivi polja kako ih korisnik vidi u formama. Ključ je zadnji dio Zod putanje,
 * pa isti naziv važi za sve rute koje koriste to polje. Polje kojeg nema ovdje
 * dobija poruku bez naziva, nikad sirovi engleski ključ.
 */
const FIELD_LABELS: Record<string, string> = {
  fullName: "Ime i prezime",
  name: "Naziv",
  title: "Naslov",
  description: "Opis",
  bio: "Opis profila (biografija)",
  content: "Sadržaj",
  excerpt: "Uvod",
  message: "Poruka",
  notes: "Napomena",
  email: "Email",
  phone: "Telefon",
  licenseNumber: "Broj licence",
  yearsExperience: "Godine iskustva",
  specializations: "Specijalizacije",
  languages: "Jezici",
  documents: "Dokumenti",
  profileImageUrl: "Fotografija",
  profileVideoUrl: "Intro video",
  imageUrl: "Slika",
  videoUrl: "Video",
  officeAddress: "Adresa ordinacije",
  sessionModality: "Način rada",
  modality: "Način rada",
  payoutMethod: "Način isplate",
  businessName: "Naziv firme/obrta",
  businessAddress: "Adresa firme/obrta",
  businessTaxId: "JIB",
  businessVatId: "PDV broj",
  personalAddress: "Adresa",
  personalTaxId: "JMBG",
  bankName: "Naziv banke",
  bankAccount: "Broj računa",
  durationMinutes: "Trajanje",
  slotDurationMinutes: "Trajanje termina",
  price: "Cijena",
  discountPrice: "Promotivna cijena",
  maxParticipants: "Broj učesnika",
  primaryProblemTags: "Glavne oblasti rada",
  secondaryProblemTags: "Dodatne oblasti rada",
  populationTags: "Populacije",
  methodTags: "Metode",
  styleTags: "Stil rada",
  notSuitableTags: "Ne preporučuj za",
  rejectionReason: "Razlog odbijanja",
  reason: "Razlog",
  company: "Firma",
  countryCode: "Zemlja",
  scheduledAt: "Termin",
  startTime: "Početak",
  endTime: "Kraj",
  date: "Datum",
  time: "Vrijeme",
  firstName: "Ime",
  lastName: "Prezime",
  clientFirstName: "Ime klijenta",
  clientLastName: "Prezime klijenta",
  goals: "Ciljevi",
  amount: "Iznos",
  note: "Napomena",
  planType: "Paket",
  maxEmployees: "Broj zaposlenih",
  billingEmail: "Email za račune",
  discountPercent: "Rabat (%)",
  initialCredit: "Početni kredit",
  rating: "Ocjena",
  defaultExpiryHours: "Rok za popunjavanje (sati)",
  minScore: "Bodovi od",
  maxScore: "Bodovi do",
  teamSize: "Veličina tima",
  topic: "Tema",
  text: "Tekst",
  label: "Naziv",
  value: "Vrijednost",
  weight: "Bodovi",
  coverImageUrl: "Naslovna slika",
  posterUrl: "Poster",
  avatarUrl: "Slika",
  slug: "Adresa (slug)",
  type: "Tip",
  changeNote: "Opis izmjene",
  tags: "Tagovi",
  question: "Pitanje",
  answer: "Odgovor",
  quote: "Citat",
  fullQuote: "Puni citat",
  role: "Uloga",
  reviewText: "Tekst recenzije",
  subject: "Naslov emaila",
  currency: "Valuta",
  vatRate: "Stopa PDV-a",
  ttlHours: "Važenje linka (sati)",
  paymentLinkTtlHours: "Važenje linka za plaćanje (sati)",
  renewalReminderDays: "Podsjetnik prije isteka (dani)",
  maxRemindersPerRun: "Broj podsjetnika po pokretanju",
  priceSemiannual: "Cijena (6 mjeseci)",
  priceAnnual: "Cijena (godišnje)",
  amountGross: "Iznos (bruto)",
  startsAt: "Početak",
  expiresAt: "Ističe",
  paidAt: "Datum uplate",
  externalId: "Referenca uplate",
  iban: "IBAN",
  taxId: "Porezni broj",
  vatId: "PDV broj",
  multiplier: "Koeficijent",
  href: "Link",
  url: "Link",
  sessions: "Termini",
  dayOfWeek: "Dan",
  startDate: "Od datuma",
  endDate: "Do datuma",
  bufferMinutes: "Pauza između termina",
  minBookingLeadDays: "Najava unaprijed (dani)",
  bookingHorizonDays: "Zakazivanje unaprijed (dani)",
  holdHours: "Rezervacija termina (sati)",
  agreedToTerms: "Saglasnost sa uslovima",
  iconUrl: "Ikona",
  sessionIds: "Seanse",
  body: "Sadržaj",
};

/**
 * Stavka liste kako je korisnik vidi: ["questions", 2, "options", 1, "label"]
 * postaje "pitanje 3, opcija 2". Lista koje nema ovdje dobija "stavka N".
 */
const ITEM_LABELS: Record<string, string> = {
  questions: "pitanje",
  options: "opcija",
  thresholds: "prag",
  sessions: "termin",
  documents: "dokument",
  links: "link",
  columns: "kolona",
  items: "stavka",
};

// Dvojezična polja: `nameBs`/`questionEn` ili ugniježđeno `{ bs, en }`.
const LANGUAGE_SUFFIX: Record<string, string> = { bs: "bosanski", en: "engleski" };

// Zodove zadane (engleske) poruke. Poruka koju je shema sama zadala ("JMBG je
// obavezan", "Mora biti tačno 13 cifara") ne počinje ovako i prolazi netaknuta.
const ZOD_DEFAULT_MESSAGE = /^(Invalid|Too (big|small)|Unrecognized|Expected|Required)/;

function valueAt(data: unknown, path: readonly PropertyKey[]): unknown {
  let current: unknown = data;
  for (const key of path) {
    if (current === null || typeof current !== "object") return undefined;
    current = (current as Record<PropertyKey, unknown>)[key];
  }
  return current;
}

function fieldLabel(key: string, parentKey: string | undefined): string | null {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  const language = LANGUAGE_SUFFIX[key];
  if (language && parentKey && FIELD_LABELS[parentKey]) return `${FIELD_LABELS[parentKey]} (${language})`;
  const suffixed = /^(.+)(Bs|En)$/.exec(key);
  if (suffixed && FIELD_LABELS[suffixed[1]!]) {
    return `${FIELD_LABELS[suffixed[1]!]} (${LANGUAGE_SUFFIX[suffixed[2]!.toLowerCase()]})`;
  }
  return null;
}

function labelFor(path: readonly PropertyKey[]): string | null {
  // Pozicija u listama: "pitanje 3, opcija 2".
  const positions: string[] = [];
  path.forEach((key, i) => {
    if (typeof key !== "number") return;
    const listKey = path[i - 1];
    const item = typeof listKey === "string" ? ITEM_LABELS[listKey] : undefined;
    positions.push(`${item ?? "stavka"} ${key + 1}`);
  });

  const last = path[path.length - 1];
  const strings = path.filter((k): k is string => typeof k === "string");
  let label: string | null = null;
  if (typeof last === "string") {
    label = fieldLabel(last, strings[strings.length - 2]);
  } else if (typeof last === "number") {
    // Element liste bez vlastitog polja, npr. ["specializations", 0].
    const listKey = path[path.length - 2];
    if (typeof listKey === "string" && !ITEM_LABELS[listKey]) label = fieldLabel(listKey, undefined);
  }

  const where = positions.join(", ");
  if (label) return where ? `${label} (${where})` : label;
  return where ? where.charAt(0).toUpperCase() + where.slice(1) : null;
}

function num(n: number | bigint): string {
  const value = Number(n);
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

/** Pravilo bez naziva polja, bezlično ("Dozvoljeno je najviše..."), da odgovara i "ti" i "vi" ekranima. */
function describeRule(issue: Issue, value: unknown): string | null {
  switch (issue.code) {
    case "too_big": {
      if (issue.origin === "string") {
        const length = typeof value === "string" ? `, a uneseno je ${value.length}` : "";
        return `Dozvoljeno je najviše ${num(issue.maximum)} znakova${length}.`;
      }
      if (issue.origin === "array" || issue.origin === "set") {
        return `Može se odabrati najviše ${num(issue.maximum)}.`;
      }
      if (issue.origin === "number" || issue.origin === "bigint") {
        return `Najveća dozvoljena vrijednost je ${num(issue.maximum)}.`;
      }
      if (issue.origin === "file") return "Fajl je prevelik.";
      return "Vrijednost je prevelika.";
    }
    case "too_small": {
      if (issue.origin === "string") {
        if (Number(issue.minimum) <= 1 || (typeof value === "string" && value.trim().length === 0)) {
          return "Polje je obavezno.";
        }
        const length = typeof value === "string" ? `, a uneseno je ${value.length}` : "";
        return `Potrebno je najmanje ${num(issue.minimum)} znakova${length}.`;
      }
      if (issue.origin === "array" || issue.origin === "set") {
        return Number(issue.minimum) === 1
          ? "Potrebno je odabrati bar jednu stavku."
          : `Potrebno je odabrati najmanje ${num(issue.minimum)}.`;
      }
      if (issue.origin === "number" || issue.origin === "bigint") {
        return `Najmanja dozvoljena vrijednost je ${num(issue.minimum)}.`;
      }
      return "Vrijednost je premala.";
    }
    case "invalid_type":
      return value === undefined || value === null ? "Polje je obavezno." : "Neispravna vrijednost.";
    case "invalid_format":
      if (issue.format === "email") return "Unesena email adresa nije ispravna.";
      if (issue.format === "url") return "Uneseni link nije ispravan.";
      if (issue.format === "datetime" || issue.format === "date" || issue.format === "time") {
        return "Datum ili vrijeme nije ispravno.";
      }
      return "Neispravan format.";
    case "invalid_value":
      return "Odabrana vrijednost nije dozvoljena.";
    case "not_multiple_of":
      return "Neispravna vrijednost.";
    case "unrecognized_keys":
      return `Zahtjev sadrži nepoznata polja: ${issue.keys.join(", ")}.`;
    default:
      return null;
  }
}

/**
 * Jedan Zod problem kao rečenica za korisnika: "Opis profila (biografija):
 * Dozvoljeno je najviše 5000 znakova, a uneseno je 5320." `data` je tijelo
 * zahtjeva, iz njega se čita koliko je stvarno uneseno.
 */
export function describeIssue(issue: Issue, data: unknown): string {
  if (issue.code === "custom") return issue.message;

  const label = labelFor(issue.path);
  const ownMessage = !ZOD_DEFAULT_MESSAGE.test(issue.message) ? issue.message : null;
  const rule = ownMessage ?? describeRule(issue, valueAt(data, issue.path)) ?? "Neispravan unos.";

  if (!label) return rule;
  if (ownMessage && ownMessage.toLowerCase().includes(label.toLowerCase())) return ownMessage;
  return `${label}: ${rule}`;
}

/** Tijelo 400 odgovora iz `validateBody`: čitljiv `error` i iste poruke po polju u `details`. */
export function formatValidationError(issues: readonly Issue[], data: unknown) {
  const details = issues.map((issue) => ({
    path: issue.path,
    code: issue.code,
    message: describeIssue(issue, data),
  }));
  const unique = [...new Set(details.map((d) => d.message))];
  return { error: unique.join(" "), details };
}

/** Za rute koje vraćaju samo prvi problem kao `error`. */
export function firstIssueMessage(issues: readonly Issue[], fallback: string, data?: unknown): string {
  const issue = issues[0];
  return issue ? describeIssue(issue, data) : fallback;
}
